/**
 * useMockupLayers Hook
 *
 * Loads and caches all layer images for a mockup template
 * Used by MockupRenderer for 5-layer compositing
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type { UIProductMockupTemplate } from '@/lib/product-transformer';
import { markImageLoaded } from '@/lib/imageLoadCache';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

export interface MockupLayers {
  baseImage: HTMLImageElement | null;
  cutoutMask: HTMLImageElement | null;
  heatheredOverlay: HTMLImageElement | null;
  textureOverlay: HTMLImageElement | null;
}

export interface UseMockupLayersResult {
  layers: MockupLayers;
  isLoading: boolean;
  error: string | null;
  progress: number; // 0-1, percentage of layers loaded
}

// ═══════════════════════════════════════════════════════════════════
// IMAGE CACHE
// ═══════════════════════════════════════════════════════════════════

// Global image cache to avoid reloading the same images
const imageCache = new Map<string, HTMLImageElement>();

/**
 * Load an image and cache it
 *
 * Note: We try loading with crossOrigin first for Canvas operations.
 * If CORS fails, we retry without crossOrigin (image will still display,
 * but Canvas operations won't work).
 *
 * @param url - Image URL to load
 * @param priority - Fetch priority: 'high' for critical images, 'low' for non-critical
 */
function loadImage(
  url: string,
  priority: 'high' | 'low' | 'auto' = 'auto'
): Promise<HTMLImageElement> {
  // Check cache first
  const cached = imageCache.get(url);
  if (cached && cached.crossOrigin === 'anonymous') {
    return Promise.resolve(cached);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();

    // Set fetch priority for browser optimization
    // 'high' = load this image before others (for active mockup in Creator)
    // 'low' = load this image later (for thumbnails, prefetch)
    if (priority !== 'auto') {
      (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority =
        priority;
    }

    // Try loading with crossOrigin first (required for Canvas export operations)
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      imageCache.set(url, img);
      markImageLoaded(url);
      resolve(img);
    };

    img.onerror = () => {
      // Fallback: try without CORS (image will display but canvas will be tainted)
      // This is a workaround for S3 buckets without CORS headers
      const fallbackImg = new Image();
      if (priority !== 'auto') {
        (
          fallbackImg as HTMLImageElement & { fetchPriority?: string }
        ).fetchPriority = priority;
      }

      fallbackImg.onload = () => {
        imageCache.set(url, fallbackImg);
        markImageLoaded(url);
        resolve(fallbackImg);
      };

      fallbackImg.onerror = () => {
        reject(new Error(`Failed to load image: ${url}`));
      };

      fallbackImg.src = url;
    };

    img.src = url;
  });
}

/**
 * Preload images in parallel
 *
 * @param urls - Array of image URLs to preload
 * @param priority - Fetch priority for all images
 */
async function preloadImages(
  urls: (string | null)[],
  priority: 'high' | 'low' | 'auto' = 'auto'
): Promise<(HTMLImageElement | null)[]> {
  const promises = urls.map((url) => {
    if (!url) return Promise.resolve(null);
    return loadImage(url, priority).catch(() => null); // Gracefully handle failures
  });

  return Promise.all(promises);
}

// ═══════════════════════════════════════════════════════════════════
// HOOK
// ═══════════════════════════════════════════════════════════════════

/**
 * Hook to load all mockup layer images
 *
 * @param template - The mockup template to load layers for
 * @returns Loading state and loaded layer images
 */
export function useMockupLayers(
  template: UIProductMockupTemplate | null | undefined
): UseMockupLayersResult {
  const [layers, setLayers] = useState<MockupLayers>({
    baseImage: null,
    cutoutMask: null,
    heatheredOverlay: null,
    textureOverlay: null,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);

  // Track current template ID to handle race conditions
  const currentTemplateId = useRef<string | null>(null);

  const loadLayers = useCallback(async (tmpl: UIProductMockupTemplate) => {
    // Set current template ID
    currentTemplateId.current = tmpl.id;

    setIsLoading(true);
    setError(null);
    setProgress(0);

    try {
      // Collect all URLs to load
      const urls = [
        tmpl.imageUrl, // Required
        tmpl.cutoutMaskUrl,
        tmpl.heatheredOverlayUrl,
        tmpl.textureOverlayUrl,
      ];

      const totalLayers = urls.filter(Boolean).length;
      let loadedCount = 0;

      // Load all images in parallel with high priority (active mockup in Creator)
      const results = await Promise.all(
        urls.map(async (url) => {
          if (!url) return null;
          try {
            // Use high priority for active Creator mockup layers
            const img = await loadImage(url, 'high');
            loadedCount++;
            setProgress(loadedCount / totalLayers);
            return img;
          } catch {
            loadedCount++;
            setProgress(loadedCount / totalLayers);
            return null;
          }
        })
      );

      const [baseImage, cutoutMask, heatheredOverlay, textureOverlay] = results;

      // Check if template changed during loading
      if (currentTemplateId.current !== tmpl.id) {
        return; // Abort if template changed
      }

      // Base image is required
      if (!baseImage) {
        throw new Error('Failed to load base mockup image');
      }

      setLayers({
        baseImage,
        cutoutMask: cutoutMask ?? null,
        heatheredOverlay: heatheredOverlay ?? null,
        textureOverlay: textureOverlay ?? null,
      });
      setProgress(1);
    } catch (err) {
      // Check if template changed during loading
      if (currentTemplateId.current !== tmpl.id) {
        return;
      }

      setError(err instanceof Error ? err.message : 'Failed to load layers');
    } finally {
      // Check if template changed during loading
      if (currentTemplateId.current === tmpl.id) {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!template) {
      setLayers({
        baseImage: null,
        cutoutMask: null,
        heatheredOverlay: null,
        textureOverlay: null,
      });
      setIsLoading(false);
      setError(null);
      setProgress(0);
      currentTemplateId.current = null;
      return;
    }

    loadLayers(template);
  }, [template, loadLayers]);

  return {
    layers,
    isLoading,
    error,
    progress,
  };
}

// ═══════════════════════════════════════════════════════════════════
// UTILITIES
// ═══════════════════════════════════════════════════════════════════

/**
 * Prefetch mockup layers for faster loading
 * Call this when hovering over a product to preload its images
 * Uses low priority to not interfere with critical loads
 */
export function prefetchMockupLayers(template: UIProductMockupTemplate): void {
  const urls = [
    template.imageUrl,
    template.cutoutMaskUrl,
    template.heatheredOverlayUrl,
    template.textureOverlayUrl,
  ].filter(Boolean) as string[];

  // Use low priority for prefetching - don't compete with active Creator loads
  preloadImages(urls, 'low').catch(() => {
    // Silently fail - prefetching is best-effort
  });
}

/**
 * Clear the image cache
 * Useful for memory management in long sessions
 */
export function clearMockupLayerCache(): void {
  imageCache.clear();
}

/**
 * Get cache statistics
 */
export function getMockupLayerCacheStats(): { size: number; urls: string[] } {
  return {
    size: imageCache.size,
    urls: Array.from(imageCache.keys()),
  };
}
